import React, { useState } from 'react';
import { DollarSign, Save, BookOpen as BookOpenIcon } from 'lucide-react';
import { useCalculator } from '../../context/CalculatorContext';
import DynamicChartSystem from './DynamicChartSystem';
import TeachingModule from '../TeachingModule';
import { teachingContent } from '../../data/teachingContent';
import { formatCurrency, shortenLargeNumber } from '../../utils/formatters';

interface IncomeChartProps {}

const IncomeChart: React.FC<IncomeChartProps> = () => {
  const { 
    investment, 
    results, 
    incomeChartVisibility, 
    toggleIncomeChartElement,
    resetIncomeChartVisibility
  } = useCalculator();
  
  const [showTeachingModule, setShowTeachingModule] = useState(false);

  if (!results) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Cargando gráficos...</p>
          <p className="text-gray-500 text-sm mt-2">Procesando datos de ingresos</p>
        </div>
      </div>
    );
  }

  // Prepare data for the chart - always include all data points
  const allChartData = results.yearlyData?.map(data => ({
    year: data.year,
    'Cosecha Capital': incomeChartVisibility.showReal ? data.realCitrusIncome : data.citrusIncome,
    'CETES': incomeChartVisibility.showReal ? (data.realCetesIncome || data.cetesIncome / Math.pow(1 + investment.inflationRate / 100, data.year)) : data.cetesIncome,
    'Ahorro Tradicional': incomeChartVisibility.showReal ? (data.realSavingsIncome || data.savingsIncome / Math.pow(1 + investment.inflationRate / 100, data.year)) : data.savingsIncome,
    'Bienes Raíces': incomeChartVisibility.showReal ? (data.realEstateRealIncome || data.realEstateIncome / Math.pow(1 + investment.inflationRate / 100, data.year)) : data.realEstateIncome,
    'Impuestos CETES': data.cetesTaxes,
    'Impuestos Ahorro': data.savingsTaxes,
    'Impuestos Bienes Raíces': data.realEstateTaxes
  })) || [];
  
  // Define lines for the chart with extremely subtle appearance when toggled off
  const chartElements = [
    {
      dataKey: 'Cosecha Capital',
      name: `Cosecha Capital ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      color: incomeChartVisibility.cosechaCapital ? '#16a34a' : '#e0e0e0',
      strokeWidth: incomeChartVisibility.cosechaCapital ? 3 : 0.5,
      fillOpacity: incomeChartVisibility.cosechaCapital ? 0.6 : 0.01,
      strokeDasharray: incomeChartVisibility.cosechaCapital ? undefined : '1 5'
    },
    {
      dataKey: 'CETES',
      name: `CETES ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      color: incomeChartVisibility.cetes ? '#0284c7' : '#e0e0e0',
      strokeDasharray: incomeChartVisibility.cetes ? '5 5' : '1 5',
      strokeWidth: incomeChartVisibility.cetes ? 2 : 0.5,
      fillOpacity: incomeChartVisibility.cetes ? 0.4 : 0.01
    },
    {
      dataKey: 'Impuestos CETES',
      name: 'Impuestos CETES',
      color: incomeChartVisibility.cetes && incomeChartVisibility.showTaxes && investment.applyTaxes ? '#0284c7' : '#e0e0e0',
      strokeDasharray: '2 2',
      strokeWidth: incomeChartVisibility.cetes && incomeChartVisibility.showTaxes && investment.applyTaxes ? 1 : 0.5,
      fillOpacity: incomeChartVisibility.cetes && incomeChartVisibility.showTaxes && investment.applyTaxes ? 0.2 : 0.01
    },
    {
      dataKey: 'Ahorro Tradicional',
      name: `Ahorro Tradicional ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      color: incomeChartVisibility.savings ? '#7c3aed' : '#e0e0e0',
      strokeDasharray: incomeChartVisibility.savings ? '5 5' : '1 5',
      strokeWidth: incomeChartVisibility.savings ? 2 : 0.5,
      fillOpacity: incomeChartVisibility.savings ? 0.4 : 0.01
    },
    {
      dataKey: 'Impuestos Ahorro',
      name: 'Impuestos Ahorro',
      color: incomeChartVisibility.savings && incomeChartVisibility.showTaxes && investment.applyTaxes ? '#7c3aed' : '#e0e0e0',
      strokeDasharray: '2 2',
      strokeWidth: incomeChartVisibility.savings && incomeChartVisibility.showTaxes && investment.applyTaxes ? 1 : 0.5,
      fillOpacity: incomeChartVisibility.savings && incomeChartVisibility.showTaxes && investment.applyTaxes ? 0.2 : 0.01
    },
    {
      dataKey: 'Bienes Raíces',
      name: `Bienes Raíces ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      color: incomeChartVisibility.realEstate ? '#ea580c' : '#e0e0e0',
      strokeDasharray: incomeChartVisibility.realEstate ? '5 5' : '1 5',
      strokeWidth: incomeChartVisibility.realEstate ? 2 : 0.5,
      fillOpacity: incomeChartVisibility.realEstate ? 0.4 : 0.01
    },
    {
      dataKey: 'Impuestos Bienes Raíces',
      name: 'Impuestos Bienes Raíces',
      color: incomeChartVisibility.realEstate && incomeChartVisibility.showTaxes && investment.applyTaxes ? '#ea580c' : '#e0e0e0',
      strokeDasharray: '2 2',
      strokeWidth: incomeChartVisibility.realEstate && incomeChartVisibility.showTaxes && investment.applyTaxes ? 1 : 0.5,
      fillOpacity: incomeChartVisibility.realEstate && incomeChartVisibility.showTaxes && investment.applyTaxes ? 0.2 : 0.01
    }
  ];

  // Prepare data for pie chart (final year composition)
  const finalYearData = results.yearlyData[results.yearlyData.length - 1];
  const pieData = [
    {
      name: `Cosecha Capital ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      value: incomeChartVisibility.showReal ? finalYearData.realCitrusIncome : finalYearData.citrusIncome,
      color: incomeChartVisibility.cosechaCapital ? '#16a34a' : '#e0e0e0'
    },
    {
      name: `CETES ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      value: incomeChartVisibility.showReal ? 
        (finalYearData.realCetesIncome || finalYearData.cetesIncome / Math.pow(1 + investment.inflationRate / 100, finalYearData.year)) : 
        finalYearData.cetesIncome,
      color: incomeChartVisibility.cetes ? '#0284c7' : '#e0e0e0'
    },
    {
      name: `Ahorro Tradicional ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      value: incomeChartVisibility.showReal ? 
        (finalYearData.realSavingsIncome || finalYearData.savingsIncome / Math.pow(1 + investment.inflationRate / 100, finalYearData.year)) : 
        finalYearData.savingsIncome,
      color: incomeChartVisibility.savings ? '#7c3aed' : '#e0e0e0'
    },
    {
      name: `Bienes Raíces ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
      value: incomeChartVisibility.showReal ? 
        (finalYearData.realEstateRealIncome || finalYearData.realEstateIncome / Math.pow(1 + investment.inflationRate / 100, finalYearData.year)) : 
        finalYearData.realEstateIncome,
      color: incomeChartVisibility.realEstate ? '#ea580c' : '#e0e0e0'
    }
  ];

  // Prepare chart system configuration
  const chartCategories = [
    {
      id: 'income',
      title: 'Evolución del Ingreso Anual',
      description: 'Crecimiento de ingresos a lo largo del tiempo con controles de animación',
      icon: <DollarSign className="w-5 h-5" />,
      color: 'from-blue-500 to-blue-600',
      availableTypes: ['line', 'bar', 'area', 'pie'] as const
    }
  ];

  const chartSystemData = {
    income: allChartData
  };

  const chartSystemElements = {
    income: chartElements
  };

  const pieChartData = {
    income: pieData
  };

  const summaryData = {
    income: [
      {
        label: `Cosecha Capital ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(incomeChartVisibility.showReal ? finalYearData.realCitrusIncome : finalYearData.citrusIncome, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: `CETES ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(incomeChartVisibility.showReal ? 
          (finalYearData.realCetesIncome || finalYearData.cetesIncome / Math.pow(1 + investment.inflationRate / 100, finalYearData.year)) : 
          finalYearData.cetesIncome, investment.currencyFormat),
        color: 'text-blue-600 bg-blue-50'
      },
      {
        label: `Ahorro Tradicional ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(incomeChartVisibility.showReal ? 
          (finalYearData.realSavingsIncome || finalYearData.savingsIncome / Math.pow(1 + investment.inflationRate / 100, finalYearData.year)) : 
          finalYearData.savingsIncome, investment.currencyFormat),
        color: 'text-purple-600 bg-purple-50'
      },
      {
        label: `Bienes Raíces ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(incomeChartVisibility.showReal ? 
          (finalYearData.realEstateRealIncome || finalYearData.realEstateIncome / Math.pow(1 + investment.inflationRate / 100, finalYearData.year)) : 
          finalYearData.realEstateIncome, investment.currencyFormat),
        color: 'text-orange-600 bg-orange-50'
      }
    ]
  };

  // Prepare visibility controls for DynamicChartSystem
  const visibilityControls = {
    income: {
      visibility: incomeChartVisibility,
      toggle: toggleIncomeChartElement
    }
  };

  // Prepare reset functions for DynamicChartSystem
  const resetVisibilityFunctions = {
    income: resetIncomeChartVisibility
  };

  const saveVisualization = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8">
          <title>Evolución del Ingreso Anual - Cosecha Capital</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              line-height: 1.5;
              margin: 0;
              padding: 20px;
              max-width: 1200px;
              margin: 0 auto;
            }
            h1 {
              color: #16a34a;
              margin-bottom: 20px;
            }
            p {
              color: #4b5563;
              margin-bottom: 30px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              padding: 12px;
              text-align: right;
              border-bottom: 1px solid #e5e7eb;
            }
            th:first-child, td:first-child {
              text-align: left;
            }
            th {
              background-color: #f0fdf4;
              color: #166534;
              font-weight: 600;
            }
            tr:nth-child(even) {
              background-color: #f9fafb;
            }
            .notes {
              background-color: #f3f4f6;
              padding: 20px;
              border-radius: 8px;
              margin-top: 30px;
            }
            .notes h3 {
              color: #374151;
              margin-bottom: 10px;
            }
            .notes ul {
              color: #4b5563;
              padding-left: 20px;
            }
          </style>
        </head>
        <body>
          <h1>Evolución del Ingreso Anual - Cosecha Capital</h1>
          <p>Análisis detallado de ingresos anuales durante ${investment.years} años de inversión.</p>
          
          <table>
            <thead>
              <tr>
                <th>Año</th>
                ${incomeChartVisibility.cosechaCapital ? 
                  `<th>Cosecha Capital${incomeChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${incomeChartVisibility.cetes ? 
                  `<th>CETES${incomeChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${incomeChartVisibility.savings ? 
                  `<th>Ahorro${incomeChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${incomeChartVisibility.realEstate ? 
                  `<th>Bienes Raíces${incomeChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${incomeChartVisibility.showTaxes && investment.applyTaxes ? '<th>Impuestos Totales</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${results.yearlyData.map(data => `
                <tr>
                  <td>${data.year}</td>
                  ${incomeChartVisibility.cosechaCapital ? 
                    `<td>${formatCurrency(incomeChartVisibility.showReal ? data.realCitrusIncome : data.citrusIncome, investment.currencyFormat)}</td>` : ''}
                  ${incomeChartVisibility.cetes ? 
                    `<td>${formatCurrency(incomeChartVisibility.showReal ? 
                      (data.realCetesIncome || data.cetesIncome / Math.pow(1 + investment.inflationRate / 100, data.year)) : 
                      data.cetesIncome, investment.currencyFormat)}</td>` : ''}
                  ${incomeChartVisibility.savings ? 
                    `<td>${formatCurrency(incomeChartVisibility.showReal ? 
                      (data.realSavingsIncome || data.savingsIncome / Math.pow(1 + investment.inflationRate / 100, data.year)) : 
                      data.savingsIncome, investment.currencyFormat)}</td>` : ''}
                  ${incomeChartVisibility.realEstate ? 
                    `<td>${formatCurrency(incomeChartVisibility.showReal ? 
                      (data.realEstateRealIncome || data.realEstateIncome / Math.pow(1 + investment.inflationRate / 100, data.year)) : 
                      data.realEstateIncome, investment.currencyFormat)}</td>` : ''}
                  ${incomeChartVisibility.showTaxes && investment.applyTaxes ? 
                    `<td>${formatCurrency(data.cetesTaxes + data.savingsTaxes + data.realEstateTaxes, investment.currencyFormat)}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="notes">
            <h3>Notas:</h3>
            <ul>
              <li>Período de análisis: ${investment.years} años</li>
              <li>Tasa de inflación considerada: ${investment.inflationRate}%</li>
              <li>Rendimiento anual Cosecha Capital: ${investment.annualProfit}%</li>
              <li>Los ingresos comienzan a partir del año 5</li>
              <li>${investment.applyTaxes ? 
                `Se consideran impuestos del ${investment.taxRate}% sobre rendimientos de inversiones tradicionales` : 
                'No se incluyen impuestos en los cálculos'}</li>
              <li>${incomeChartVisibility.showReal ? 
                'Los valores mostrados están ajustados por inflación (valores reales)' : 
                'Los valores mostrados son nominales (sin ajustar por inflación)'}</li>
            </ul>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cosecha_capital_ingresos_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-green-800">Evolución del Ingreso Anual</h3>
        <div className="flex items-center gap-3">
          <button
            onClick={saveVisualization}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title="Guardar esta visualización como HTML"
          >
            <Save className="w-5 h-5" />
            <span>Guardar</span>
          </button>
          <button
            onClick={() => setShowTeachingModule(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            title="Abrir tutorial de esta sección"
          >
            <BookOpenIcon className="w-5 h-5" />
            <span>Tutorial</span>
          </button>
        </div>
      </div>
      
      {/* Dynamic Chart System */}
      <div className="flex-1 min-h-[420px]">
        <DynamicChartSystem
          categories={chartCategories}
          chartData={chartSystemData}
          chartElements={chartSystemElements}
          pieData={pieChartData}
          formatters={{
            yLabel: (value) => shortenLargeNumber(value),
            tooltip: (value) => formatCurrency(value, investment.currencyFormat)
          }}
          summaryData={summaryData}
          investment={investment}
          multiplierValidChartIds={['income']}
          visibilityControls={visibilityControls}
          resetVisibilityFunctions={resetVisibilityFunctions}
          hideAdditionalOptions={true}
        />
      </div>
      
      {/* Teaching Module */}
      <TeachingModule
        title={teachingContent.income.title}
        steps={teachingContent.income.steps}
        isOpen={showTeachingModule}
        onClose={() => setShowTeachingModule(false)}
      />
    </div>
  );
};

export default IncomeChart;